import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useRef, useState } from 'react';
import { db } from '../../../config/firebase';

const PRESENCE_COLLECTION = 'presence';
const ONLINE_THRESHOLD_MS = 35_000;
const RECHECK_INTERVAL_MS = 10_000;

export const useUserPresence = (userId: string | null | undefined): boolean => {
  const [isOnline, setIsOnline] = useState(false);
  const lastSeenRef = useRef<Date | null>(null);

  useEffect(() => {
    if (!userId) {
      setIsOnline(false);
      return;
    }

    const evaluate = (lastSeen: Date | null) => {
      setIsOnline(!!lastSeen && Date.now() - lastSeen.getTime() < ONLINE_THRESHOLD_MS);
    };

    const unsubscribe = onSnapshot(
      doc(db, PRESENCE_COLLECTION, userId),
      (snapshot) => {
        if (!snapshot.exists()) {
          lastSeenRef.current = null;
          setIsOnline(false);
          return;
        }
        const data = snapshot.data();
        lastSeenRef.current = data?.lastSeen?.toDate() ?? null;
        evaluate(lastSeenRef.current);
      },
      (error) => {
        // Without this handler the failure escapes the SDK and takes the whole
        // React tree down with it. Presence is decoration: if it cannot be read,
        // the member simply shows as offline.
        console.warn('Presence listener stopped:', error.message);
        setIsOnline(false);
      }
    );

    const interval = setInterval(() => evaluate(lastSeenRef.current), RECHECK_INTERVAL_MS);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [userId]);

  return isOnline;
};
